import type { BuilderPost, BuilderSlot, WeekGrid } from "../../data/schedule-builder";
import { SLOT_TONE_COPY } from "../../data/schedule-tone";
import "./manager-calendar.css";

export interface ManagerCalendarProps {
  grid: WeekGrid;
  timeZone: string;
  selectedDate: string;
  onSelectDate: (date: string) => void;
  /** Mobile agenda navigation selects a day without opening the planning panel. */
  onBrowseDate?: (date: string) => void;
  onSelectSlot: (slot: BuilderSlot, siteName: string, routeName: string) => void;
}

// These are service-date labels, not instants. Formatting in the display timezone would
// move the date in zones such as Pacific/Kiritimati; only shift windows use that zone.
function dateLabel(date: string, options: Intl.DateTimeFormatOptions): string {
  const anchor = new Date(`${date}T12:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(anchor.getTime())) return date;
  return new Intl.DateTimeFormat("en-US", { ...options, timeZone: "UTC" }).format(anchor);
}

function fullDate(date: string): string {
  return dateLabel(date, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function slotsForDate(post: BuilderPost, date: string): BuilderSlot[] {
  return post.days.find((day) => day.date === date)?.slots ?? [];
}

function ShiftButton({
  slot,
  date,
  post,
  onSelect,
}: {
  slot: BuilderSlot;
  date: string;
  post: BuilderPost;
  onSelect: ManagerCalendarProps["onSelectSlot"];
}) {
  const status = SLOT_TONE_COPY[slot.tone];
  return (
    <button
      type="button"
      className={`manager-calendar__shift manager-calendar__shift--${slot.tone}`}
      aria-label={`View shift: ${post.siteName}, ${post.routeName}, ${fullDate(date)}, ${slot.window}, ${slot.fraction} assigned, ${status}`}
      onClick={() => onSelect(slot, post.siteName, post.routeName)}
    >
      <span className="manager-calendar__window">{slot.window}</span>
      <span className="manager-calendar__staffing">{slot.fraction} assigned</span>
      <span className="manager-calendar__status">{status}</span>
      {slot.credentials.length > 0 ? (
        <span className="manager-calendar__credentials">
          {slot.credentials.map((credential) => (
            <span key={credential}>{credential.replaceAll("_", " ")}</span>
          ))}
        </span>
      ) : null}
    </button>
  );
}

function EmptyDateButton({
  date,
  onSelect,
  context,
}: {
  date: string;
  onSelect: ManagerCalendarProps["onSelectDate"];
  context?: string;
}) {
  return (
    <button
      type="button"
      className="manager-calendar__empty-date"
      aria-label={`Select ${fullDate(date)}${context ? ` for ${context}` : ""}`}
      onClick={() => onSelect(date)}
    >
      <span className="manager-calendar__empty-icon" aria-hidden="true">+</span>
      <span>Select date</span>
    </button>
  );
}

export function ManagerCalendar({ grid, timeZone, selectedDate, onSelectDate, onBrowseDate = onSelectDate, onSelectSlot }: ManagerCalendarProps) {
  const agendaDate = grid.days.includes(selectedDate) ? selectedDate : grid.days[0];
  const agendaPosts = agendaDate === undefined ? [] : grid.posts.filter((post) => slotsForDate(post, agendaDate).length > 0);

  return (
    <section className="manager-calendar" aria-label="Shift calendar">
      <div className="manager-calendar__context">
        <p className="manager-calendar__timezone">Times shown in <strong>{timeZone}</strong></p>
        <p className="manager-calendar__hint">Select a shift to view details and assign staff.</p>
      </div>

      <div className="manager-calendar__desktop">
        <div className="manager-calendar__scroll" tabIndex={0} aria-label="Weekly shifts; scroll horizontally for all dates">
          <table className="manager-calendar__table">
            <caption className="manager-calendar__sr-only">Monday service week beginning {fullDate(grid.weekStart)}. Shifts grouped by site and route.</caption>
            <thead>
              <tr>
                <th scope="col" className="manager-calendar__post-heading">Site &amp; route</th>
                {grid.days.map((date) => (
                  <th scope="col" key={date} className={date === selectedDate ? "manager-calendar__day--selected" : ""}>
                    <button
                      type="button"
                      className="manager-calendar__day"
                      aria-label={`Select ${fullDate(date)}`}
                      aria-pressed={date === selectedDate}
                      onClick={() => onSelectDate(date)}
                    >
                      <span>{dateLabel(date, { weekday: "short" })}</span>
                      <strong>{dateLabel(date, { month: "short", day: "numeric" })}</strong>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.posts.length > 0 ? grid.posts.map((post) => (
                <tr key={JSON.stringify([post.siteName, post.routeName])}>
                  <th scope="row" className="manager-calendar__post" aria-label={`${post.siteName}, ${post.routeName}`}>
                    <strong>{post.siteName}</strong>
                    <span>{post.routeName}</span>
                  </th>
                  {grid.days.map((date) => {
                    const slots = slotsForDate(post, date);
                    return (
                      <td key={date} className={date === selectedDate ? "manager-calendar__day--selected" : ""}>
                        <div className="manager-calendar__cell">
                          {slots.length > 0 ? slots.map((slot) => (
                            <ShiftButton key={slot.id} slot={slot} date={date} post={post} onSelect={onSelectSlot} />
                          )) : (
                            <EmptyDateButton date={date} onSelect={onSelectDate} context={`${post.siteName}, ${post.routeName}`} />
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              )) : (
                <tr>
                  <th scope="row" className="manager-calendar__post manager-calendar__post--empty">No shifts in the loaded week</th>
                  {grid.days.map((date) => (
                    <td key={date} className={date === selectedDate ? "manager-calendar__day--selected" : ""}>
                      <div className="manager-calendar__cell"><EmptyDateButton date={date} onSelect={onSelectDate} /></div>
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="manager-calendar__mobile">
        <div className="manager-calendar__date-strip" role="group" aria-label="Choose agenda date">
          {grid.days.map((date) => (
            <button
              key={date}
              type="button"
              className="manager-calendar__agenda-date"
              aria-label={`Show ${fullDate(date)}`}
              aria-pressed={date === agendaDate}
              onClick={() => onBrowseDate(date)}
            >
              <span>{dateLabel(date, { weekday: "short" })}</span>
              <strong>{dateLabel(date, { day: "numeric" })}</strong>
            </button>
          ))}
        </div>
        {agendaDate !== undefined ? (
          <section className="manager-calendar__agenda" aria-label={`Shifts for ${fullDate(agendaDate)}`}>
            <h3>{dateLabel(agendaDate, { weekday: "long", month: "long", day: "numeric" })}</h3>
            {agendaPosts.length > 0 ? agendaPosts.map((post) => (
              <section className="manager-calendar__agenda-post" key={JSON.stringify([post.siteName, post.routeName])} aria-label={`${post.siteName}, ${post.routeName}`}>
                <div className="manager-calendar__agenda-heading"><h4>{post.siteName}</h4><p>{post.routeName}</p></div>
                {slotsForDate(post, agendaDate).map((slot) => (
                  <ShiftButton key={slot.id} slot={slot} date={agendaDate} post={post} onSelect={onSelectSlot} />
                ))}
              </section>
            )) : (
              <div className="manager-calendar__agenda-empty">
                <p>No shifts in this loaded date.</p>
                <EmptyDateButton date={agendaDate} onSelect={onSelectDate} />
              </div>
            )}
          </section>
        ) : <p className="manager-calendar__no-dates">No calendar dates loaded.</p>}
      </div>
    </section>
  );
}
